<script setup>
    // Common
    import i18n from '@/common/i18n';

    // Utis
    import utils from '@/commonProject/helpers/utils';

    // Vue
    import { ref, defineEmits, inject, defineExpose } from 'vue';

    // Store
    import { useSuerveyStore } from '@/stores/reserve/suerveyStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    //現在のページ
    const nowPage = ref(1);
    //総ページ数
    const totalPage = ref(1);

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // Storeクラスを読み込む。
    const suerveyStore = useSuerveyStore();

    // アンケート回答入力モーダルの表示・非表示を管理する。
    const suerveyApplyDispFlg = ref('');
    // アンケート回答確認の表示・非表示を管理する。
    const suerveyConfirmDispFlg = ref('');
    // アンケート回答完了の表示・非表示を管理する。
    const suerveyCompleteDispFlg = ref('');

    // APIで取得した、本モーダル内で使用するアンケートフォームの構造。
    const suerveyFormList = ref([]);

    // 本モーダル内でアンケートフォームに回答した情報を保管する。
    const suerveyFormAnswer = ref([]);

    // 現在表示しているフォームの座標データ。
    const suerveyFormView = ref({
        FormNm: '',
        FormAttr: [],
    });

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');
    const reservationPk = ref('');
    const webCngPk = ref('');
    const questionaryPk = ref(null);
    const lang = ref('');

    // アンケートが回答済みかを判定し、画面の表示を制御するフラグの変数。
    const isAlreadyAnsweredForm = ref(false);

    // エラーが発生しているかを判定し、画面の表示を制御するフラグの変数。
    const isErrorForm = ref(false);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    // 呼び出し元の親画面のアンケートモーダルを閉じる処理を定義する。
    const emit = defineEmits(['suerveyApplyModal:close', 'suerveyApplyModal:update']);
    function close() {
        // 変数を初期化する。
        reset();

        // 親画面から継承中の処理を実行する。
        emit('suerveyApplyModal:close');
    }
    function update() {
        // 変数を初期化する。
        reset();

        // 親画面から継承中の処理を実行する。
        emit('suerveyApplyModal:update');
    }

    // ***** アンケート回答入力モーダルの処理 - Start - ***** //
    /**
     * アンケートフォーム取得APIをコールし、画面のアンケート回答入力モーダルに表示する。
     */
    async function getSuerveyForm() {
        // 変数を初期化する。
        reset();

        // アンケートフォーム取得APIをのクエリパラメーターを生成する。
        let queryParam = {
            Lang: lang.value,
            //ReservationPk: reservationPk.value,
            //WebCngPk: webCngPk.value,
        };

        // 前画面から予約PKを取得した場合は、クエリパラメータに条件に設定する。
        if (!utils.isNullEmpty(reservationPk.value)) {
            queryParam['ReservationPk'] = reservationPk.value;
        }

        // 前画面からWeb相談PKを取得した場合は、クエリパラメータに条件に設定する。
        if (!utils.isNullEmpty(webCngPk.value)) {
            queryParam['WebCngPk'] = webCngPk.value;
        }

        // 前画面からアンケートPKを取得した場合は、クエリパラメータに条件に設定する。
        // ホーム画面のお知らせモーダルのアンケートボタンをクリックして、いずれの予約PK、Web相談PKも存在しない、アンケートPKのみ場合のルート。
        if (!utils.isNullEmpty(questionaryPk.value)) {
            queryParam['QuestionaryPk'] = questionaryPk.value;
        }

        // Storeクラスを経由してアンケートフォーム取得APIをコールする。
        const response = await suerveyStore.getSuerveyForm(API_URL.value, queryParam);

        if (response.success) {
            // アンケートフォーム取得APIが正常応答の場合、ステータスに応じて、次の通りに判定する。
            switch (response.responseBody.ApiRes.Status) {
                case 0:
                    {
                        // 正常応答

                        // フォームなどの変数の初期化は、上部で実施しているので、正常応答ではあえて行わない。

                        // APIから取得した相談後アンケートフォームの総個数を初期化する。
                        totalPage.value = 0;

                        // APIから取得したフォーム構造を、画面内フォーム構造解析用配列にコピーする。
                        // 次へや前へボタン等で、複数のフォーム構造を行き来するので、フォーム全体構造を保管しておく。
                        suerveyFormList.value = JSON.parse(JSON.stringify(suerveyStore.suerveyForm.List));

                        // アンケートのフォーム構造が存在するか判定する。
                        if (!utils.isNullEmptyArray(suerveyFormList.value)) {
                            // フォーム構造が存在する場合、フォームの回答構造を生成するために、相談後アンケート全体をループして回答用フォーム構造を生成する。
                            for (const list of suerveyFormList.value) {
                                let answerList = [];
                                for (const formAttr of list.FormAttr) {
                                    let Values = null;

                                    // アンケート項目の起票内容に応じて、次の通り、画面フォームからの入力内容を格納するモデルを作成する。
                                    switch (formAttr.Type) {
                                        case '01':
                                        case '02':
                                            // ラジオボックスの場合。
                                            // チェックボックスの場合。
                                            Values = [];
                                            break;
                                        default:
                                            // 上記以外の場合。
                                            // テキストボックスなど。
                                            Values = '';
                                            break;
                                    }

                                    const answerForm = {
                                        Id: formAttr.Id,
                                        Nm: formAttr.Nm,
                                        Type: formAttr.Type,
                                        Values: Values,
                                        Required: formAttr.Required,
                                    };
                                    answerList.push(answerForm);
                                }
                                suerveyFormAnswer.value.push(answerList);
                            }

                            // 最大フォーム数を計算する。
                            totalPage.value = suerveyFormList.value.length;

                            // 回答するアンケートフォームを画面に描画する。
                            // 初期表示では、始めのアンケートフォームを指定する。
                            createFormView(0);
                        }
                    }
                    break;
                case 2:
                    {
                        // 回答済みのアンケートの場合。

                        // アンケートを画面に表示しない。
                        // 回答済みの文言を画面に表示する。
                        isAlreadyAnsweredForm.value = true;
                    }
                    break;
                default:
                    {
                        // 上記のいずれにも該当しない場合。

                        // 画面の描画をエラールートとして処理する。
                        isErrorForm.value = true;

                        // APIからエラーメッセージが返却された場合。
                        if (!utils.isNullEmpty(response.responseBody.ApiRes.MessageJp) && lang.value === '1') {
                            // 画面の表示モードが「日本語」の場合。
                            messageDialog('error', response.responseBody.ApiRes.MessageJp);
                        } else if (!utils.isNullEmpty(response.responseBody.ApiRes.MessageEn) && lang.value === '2') {
                            // 画面の表示モードが「英語」の場合。
                            messageDialog('error', response.responseBody.ApiRes.MessageEn);
                        } else {
                            // APIで例外エラーが発生したが、専用のエラーメッセージが返却されなかった場合。
                            // 画面で共通的なエラーメッセージを表示する。
                            messageDialog('error', i18n.global.t('common.message.apiResponseUnmatchStatus'));
                        }
                    }
                    break;
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    } // #getSuerveyForm

    /**
     *
     * @param pageNumber
     */
    async function createFormView(pageNumber) {
        // APIから取得した相談後アンケートの格納変数。
        const A = JSON.parse(JSON.stringify(suerveyFormList.value[pageNumber]));

        // 相談後アンケートが存在するかどうか判定する。
        if (!utils.isNull(A)) {
            let B = {
                FormNm: A.FormNm,
                FormAttr: [],
            };

            for (const formAttr of A.FormAttr) {
                if (!utils.isNullEmptyArray(formAttr)) {
                    // プルダウン、チェックボックス、ラジオボタンの選択肢を作成する。
                    let ChoicesArr = [];
                    if (!utils.isNullEmptyArray(formAttr.Choices)) {
                        for (const choice of formAttr.Choices) {
                            let obj = {};

                            const index = choice.indexOf(':');

                            // コロンの区切りIDと名称の区切り文字が存在するか判定する。
                            if (index !== -1) {
                                // コロンの区切り文字が存在する場合は、存在する座標が返却される。
                                // 選択肢のID部と名称部を分離する。
                                obj = {
                                    id: choice.substr(0, index), // コロンの位置までを抽出しIDとして設定する。
                                    name: choice.substr(index + 1), // コロンの座標の次の文字列以降を全て名称として抽出する。
                                    value: choice,
                                };
                            } else {
                                // コロンの区切り文字ば存在しない場合は-1が返る。
                                // APIの取得情報をそのまま格納する。
                                obj = {
                                    id: choice,
                                    name: choice,
                                    value: choice,
                                };
                            }

                            // 選択肢の情報を格納する。
                            ChoicesArr.push(obj);
                        }
                    }

                    const model = {
                        Choices: ChoicesArr,
                        Type: formAttr.Type,
                        No: formAttr.No,
                        Nm: formAttr.Nm,
                        Id: formAttr.Id,
                        Explain: formAttr.Explain,
                    };

                    B.FormAttr.push(model);
                }
            }
            suerveyFormView.value = B;
        }
    }

    /**
     * フォームの必須入力をチェックする。
     */
    async function checkFromSuerveyAnswer() {
        let message = '';
        const thisServey = suerveyFormAnswer.value[nowPage.value - 1];
        for (const value of thisServey) {
            if (value.Required) {
                switch (value.Type) {
                    case '01':
                    case '02':
                        // ラジオボタン
                        // チェックボックス
                        if (utils.isEmptyArray(value.Values)) {
                            message += value.Nm + i18n.global.t('common.message.formErrorMessage1') + '\n';
                        }
                        break;
                    default:
                        // 上記以外
                        if (utils.isNullEmptyBlank(value.Values)) {
                            message += value.Nm + i18n.global.t('common.message.formErrorMessage2') + '\n';
                        }
                        break;
                }
            }

            // フォーマットチェック
            switch (value.Type) {
                case '04':
                    // テキストボックス
                    if (!utils.isNullEmptyBlank(value.Values)) {
                        // 文字数超過を判定
                        if (utils.checkLengthOfString(value.Values)) {
                            message += value.Nm + i18n.global.t('common.message.formErrorMessage3') + '\n';
                        }
                    }
                    break;
                case '05':
                    // テキストエリア
                    if (!utils.isNullEmptyBlank(value.Values)) {
                        // 文字数超過を判定
                        if (utils.checkLengthOfLongString(value.Values)) {
                            message += value.Nm + i18n.global.t('common.message.formErrorMessage4') + '\n';
                        }
                    }
                    break;
                case '08':
                    // 数値
                    if (!utils.isNullEmptyBlank(value.Values)) {
                        // int32の範囲超過を判定
                        if (utils.checkLengthOfDisplayOrderNumber(value.Values)) {
                            message += value.Nm + i18n.global.t('common.message.formErrorMessage5') + '\n';
                        }
                    }
                    break;
                default:
                    // 何もしない
                    break;
            }
        }

        // エラーメッセージが存在する場合は、先に進ませない。
        if (message !== '') {
            messageDialog('error', message);
            return true;
        }

        return false;
    }
    async function applyTheFormAndMoveToSuerveyConfirmModal() {
        const isError = await checkFromSuerveyAnswer();
        if (isError) {
            return;
        }

        suerveyApplyDispFlg.value = 'none';
        suerveyConfirmDispFlg.value = 'block';
        suerveyCompleteDispFlg.value = 'none';
    } // #applyTheFormAndMoveToSuerveyConfirmModal
    // ***** アンケート回答入力モーダルの処理 - End - ***** //

    /**
     * アンケートフォーム登録APIをコールし、画面のアンケート回答完了モーダルを表示する。
     */
    async function applyTheFormAndMoveToSuerveyCompleteModal() {
        const isError = await checkFromSuerveyAnswer();
        if (isError) {
            return;
        }
        
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // アンケートフォーム登録APIのリクエストボディを生成する。
        let requestBody = {
            List: [],
        };

        for (let i = 0; i < suerveyFormList.value.length; i++) {
            const list = suerveyFormList.value[i];
            // APIに送信するフォーム情報を作成する
            let requestBodyList = {
                QuestionaryPk: list.QuestionaryPk,
                FormPk: list.FormPk,
                FormData: [],
            };
            for (const answer of suerveyFormAnswer.value[i]) {
                let value = [];
                switch (answer.Type) {
                    case '01':
                    case '02':
                        // ラジオボタン
                        // チェックボックス
                        if (!utils.isNullEmptyArray(answer.Values)) {
                            for (const A of answer.Values) {
                                value.push(A);
                            }
                        }
                        break;
                    case '08':
                        // 数値
                        if (!utils.isNullEmpty(answer.Values)) {
                            value.push(String(answer.Values));
                        }
                        break;
                    default:
                        // 上記以外
                        if (!utils.isNullEmpty(answer.Values)) {
                            value.push(answer.Values);
                        }
                        break;
                }

                if (utils.isNullEmptyArray(value)) {
                    value = null;
                }
                const formData = {
                    Id: answer.Id,
                    Values: value,
                };
                requestBodyList.FormData.push(formData);
            }
            requestBody.List.push(requestBodyList);
        }

        const response = await suerveyStore.putSuerveyForm(API_URL.value, requestBody);
        if (response.success) {
            suerveyApplyDispFlg.value = 'none';
            suerveyConfirmDispFlg.value = 'none';
            suerveyCompleteDispFlg.value = 'block';
            reset();
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    } // #applyTheFormAndMoveToSuerveyCompleteModal

    /**
     * アンケート回答入力モーダル内で、次のページに進む処理
     */
    async function nextPage() {
        const isError = await checkFromSuerveyAnswer();
        if (isError) {
            return;
        }

        //次のページに移動する関数
        if (nowPage.value < totalPage.value) {
            nowPage.value += 1;
        }

        // 指定した座標のフォームを画面に表示する。
        //suerveyFormView.value = suerveyFormList.value[nowPage.value - 1];

        // 指定した座標のアンケートフォームを画面に描画する。
        createFormView(nowPage.value - 1);
    } // #nextPage

    /**
     * アンケート回答入力モーダル内で、前のページに戻る処理
     */
    function prevPage() {
        //前のページに移動する関数
        if (nowPage.value > 1) {
            nowPage.value -= 1;
        }

        // 指定した座標のフォームを画面に表示する。
        //suerveyFormView.value = suerveyFormList.value[nowPage.value - 1];

        // 指定した座標のアンケートフォームを画面に描画する。
        createFormView(nowPage.value - 1);
    } // #prevPage
    // ***** アンケート回答入力確認モーダルの処理 - End - ***** //

    /**
     * アンケート回答確認モーダルの「戻る」ボタンのクリックイベント。
     * アンケート回答入力モーダルに戻る。
     */
    function backToSuerveyApplyModal() {
        suerveyConfirmDispFlg.value = 'none';
        suerveyApplyDispFlg.value = 'block';
    } // #backToSuerveyApplyModal

    /**
     * 親画面からアンケート回答入力モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @param {String} flg1 Langの文字列を取得する。
     * @param {String} flg2 ReservationPkの文字列を取得する。
     * @param {String} flg3 WebCngPkの文字列を取得する。
     * @param {String} flg4 WebCngPkの文字列を取得する。
     * @returns なし
     */
    async function openModal(trg, flg1, flg2, flg3, flg4) {
        // 変数を初期化する。
        reset();

        // モーダルの表示を初期化する。登録モーダルの表示に切り替えて、他モーダルは全て閉じる。
        suerveyApplyDispFlg.value = 'block';
        suerveyConfirmDispFlg.value = 'none';
        suerveyCompleteDispFlg.value = 'none';

        // APIのURL情報を取得する。
        API_URL.value = trg;
        lang.value = flg1;

        if (!utils.isNullEmpty(flg2)) {
            reservationPk.value = flg2;
        }

        if (!utils.isNullEmpty(flg3)) {
            webCngPk.value = flg3;
        }

        if (!utils.isNullEmpty(flg4)) {
            questionaryPk.value = flg4;
        }

        // Web相談一覧取得をコールする。
        await getSuerveyForm();

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalSurveyAnswer .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    } // #openModal

    /**
     * 本モーダル内の各変数と、Storeクラス内の関連変数を初期化する。
     */
    function reset() {
        // 最大ページ数初期化
        totalPage.value = 1;

        // 現在のフォームページの座標を初期化。
        nowPage.value = 1;

        // Storeクラス内の変数を初期化する。
        suerveyStore.suerveyForm = [];
        suerveyStore.suerveyFormAnswer = [];

        // 本コンポーネント内の変数を初期化する。
        suerveyFormAnswer.value = [];
        suerveyFormList.value = [];

        // 現在の表示しているフォーム情報の変数を初期化する。
        suerveyFormView.value = {
            FormNm: '',
            FormAttr: [],
        };

        // 画面表示モードのフラグを初期化する。
        isAlreadyAnsweredForm.value = false;
        isErrorForm.value = false;
    } // #reset

    /**
     * 親画面から実行を許可する本コンポーネント内のメソッドの一覧。
     */
    defineExpose({
        openModal,
    });
    // ***** 各アンケートモーダルの共通的な処理 - Start - ***** //
</script>

<style></style>

<template>
    <article id="modalSurveyAnswer" class="modal" :style="{ display: suerveyApplyDispFlg }">
        <h3>{{ i18n.global.t('surveyApply.parts.modalTitle1') }}</h3>
        <section class="modalContents" :class="{ double: nowPage === 1 || nowPage === totalPage }">
            <template v-if="isAlreadyAnsweredForm">
                <!--回答済みアンケート-->
                <span style="width: 100%; text-align: center; display: block; font-size: 26px; margin-top: 80px; color: #505050">{{ i18n.global.t('surveyApply.parts.modalInformation1') }}</span>
            </template>
            <template v-else>
                <template v-if="!isErrorForm">
                    <!--回答可能なアンケートフォーム-->
                    <span class="surveyTitle">{{ suerveyFormView.FormNm }}</span>
                    <table class="survey">
                        <tbody>
                            <tr v-for="(FormAttr, index) in suerveyFormView.FormAttr" :key="index" :id="'tr-' + (index + 1)">
                                <td>
                                    <span>{{ FormAttr.No }}</span>
                                </td>
                                <td>
                                    <span>{{ FormAttr.Nm }}</span>
                                </td>
                                <td>
                                    <template v-if="FormAttr.Type === '01'">
                                        <!--ラジオボタンを生成-->
                                        <ul class="blockInfoBox">
                                            <li v-for="(data, index2) in FormAttr.Choices" :key="index + '-' + index2">
                                                <input type="radio" :id="'suervey-radio-' + (index + 1) + '-' + (index2 + 1)" class="radio" :name="'suervey-radioGroup-' + (index + 1)" v-model="suerveyFormAnswer[nowPage - 1][index].Values" :value="data.id" />
                                                <label class="radio label" :for="'suervey-radio-' + (index + 1) + '-' + (index2 + 1)">{{ data.name }}</label>
                                            </li>
                                        </ul>
                                    </template>
                                    <template v-else-if="FormAttr.Type === '02'">
                                        <!--チェックボックスを生成-->
                                        <ul class="blockInfoBox">
                                            <li v-for="(data, index2) in FormAttr.Choices" :key="index + '-' + index2">
                                                <input type="checkbox" :id="'suervey-checkbox-' + (index + 1) + '-' + (index2 + 1)" class="checkbox" v-model="suerveyFormAnswer[nowPage - 1][index].Values" :value="data.id" />
                                                <label class="checkbox label" :for="'suervey-checkbox-' + (index + 1) + '-' + (index2 + 1)">{{ data.name }}</label>
                                            </li>
                                        </ul>
                                    </template>
                                    <template v-else-if="FormAttr.Type === '04'">
                                        <!--テキストボックスを生成-->
                                        <input type="text" v-model="suerveyFormAnswer[nowPage - 1][index].Values" />
                                    </template>
                                    <template v-else-if="FormAttr.Type === '05'">
                                        <!--テキストエリアを生成-->
                                        <textarea v-model="suerveyFormAnswer[nowPage - 1][index].Values" style="height: 68px" />
                                    </template>
                                    <template v-else-if="FormAttr.Type === '08'">
                                        <!--整数値を生成-->
                                        <input type="number" v-model="suerveyFormAnswer[nowPage - 1][index].Values" />
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>
            </template>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li :class="{ unvisibleBtn: nowPage !== 1 }">
                    <!--キャンセル-->
                    <button type="button" class="btnNormal" @click="close">{{ i18n.global.t('common.parts.cancel') }}</button>
                </li>
                <li :class="{ unvisibleBtn: nowPage === 1 }">
                    <!--前へ-->
                    <button type="button" class="btnSecondary" @click="prevPage()">{{ i18n.global.t('surveyApply.parts.modalButton1') }}</button>
                </li>
                <li :class="{ unvisibleBtn: nowPage === totalPage }" style="margin-right: unset">
                    <!--次へ-->
                    <button type="button" class="btnSecondary" @click="nextPage()">{{ i18n.global.t('surveyApply.parts.modalButton2') }}</button>
                </li>
                <li :class="{ unvisibleBtn: nowPage !== totalPage && totalPage !== 1 }">
                    <!--登録-->
                    <button type="button" class="btnPrimary" @click="applyTheFormAndMoveToSuerveyConfirmModal()" :disabled="isAlreadyAnsweredForm || isErrorForm">{{ i18n.global.t('common.parts.entry') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--アンケート回答入力（相談後アンケート）-->
    <article id="modalSurveyAnswerCheck" class="modal small" :style="{ display: suerveyConfirmDispFlg }">
        <p class="textCenter">{{ i18n.global.t('surveyConfirm.parts.modalTitle1') }}</p>
        <section class="dialogFooter">
            <ul class="btnArea center">
                <li>
                    <!--いいえ-->
                    <button type="button" class="btnNormal" @click="backToSuerveyApplyModal()">{{ i18n.global.t('surveyConfirm.parts.modalButton2') }}</button>
                </li>
                <li>
                    <!--はい-->
                    <button type="button" class="btnPrimary" @click="applyTheFormAndMoveToSuerveyCompleteModal()" :disabled="disabledStore.isDisabled">{{ i18n.global.t('surveyConfirm.parts.modalButton1') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--アンケート回答確認（相談後アンケート）-->
    <article id="modalSurveyAnswerComplete" class="modal small" :style="{ display: suerveyCompleteDispFlg }">
        <p class="textCenter">{{ i18n.global.t('surveyComplete.parts.modalTitle1') }}</p>
        <section class="dialogFooter">
            <ul class="btnArea center">
                <li>
                    <!--閉じる-->
                    <button type="button" class="btnNormal" @click="update()">{{ i18n.global.t('common.parts.close') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--アンケート回答完了（相談後アンケート）-->
</template>
